import React, { useState } from 'react';
import { KeywordDiscovery as KeywordDiscoveryType, discoverKeywords, addKeyword } from '@/api/seoApi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Search, Plus, RefreshCw, TrendingUp, CheckCircle2, HelpCircle } from 'lucide-react';

interface KeywordDiscoveryProps {
  siteId: string;
  onKeywordAdded: () => void;
}

export function KeywordDiscovery({ siteId, onKeywordAdded }: KeywordDiscoveryProps) {
  const [discoveries, setDiscoveries] = useState<KeywordDiscoveryType[]>([]);
  const [discovering, setDiscovering] = useState(false);
  const [addingKeyword, setAddingKeyword] = useState<string | null>(null);
  const [addingAll, setAddingAll] = useState(false);

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const data = await discoverKeywords(siteId, 50);
      setDiscoveries(data);
    } catch (error) {
      console.error('Failed to discover keywords:', error);
      alert('Failed to discover keywords');
    } finally {
      setDiscovering(false);
    }
  };

  const handleAddKeyword = async (keyword: string) => {
    setAddingKeyword(keyword);
    try {
      await addKeyword(siteId, keyword, {
        searchEngine: 'google',
        location: 'United States',
        device: 'desktop',
        checkFrequency: 'daily'
      });

      onKeywordAdded();
    } catch (error) {
      console.error('Failed to add keyword:', error);
      alert('Failed to add keyword');
    } finally {
      setAddingKeyword(null);
    }
  };

  const handleAddAll = async () => {
    setAddingAll(true);
    let successCount = 0;
    let failCount = 0;

    for (const discovery of discoveries) {
      try {
        await addKeyword(siteId, discovery.keyword, {
          searchEngine: 'google',
          location: 'United States',
          device: 'desktop',
          checkFrequency: 'daily'
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to add keyword "${discovery.keyword}":`, error);
        failCount++;
      }
    }

    setAddingAll(false);

    if (failCount > 0) {
      alert(`Added ${successCount} keywords. ${failCount} failed.`);
    } else {
      alert(`Successfully added all ${successCount} keywords!`);
    }

    onKeywordAdded();
  };

  const renderPositionBadge = (position: number) => {
    if (position <= 3) {
      return <Badge className="bg-green-500 hover:bg-green-600">#{position}</Badge>;
    }
    if (position <= 10) {
      return <Badge className="bg-blue-500 hover:bg-blue-600">#{position}</Badge>;
    }
    if (position <= 20) {
      return <Badge className="bg-yellow-500 hover:bg-yellow-600">#{position}</Badge>;
    }
    return <Badge variant="outline">#{position}</Badge>;
  };

  if (!discovering && discoveries.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Keyword Discovery
          </CardTitle>
          <CardDescription>
            Find keywords that your site already ranks for based on your product catalog
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              Automatically test keywords from your products and content to find what you're already ranking for.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              This will test up to 50 potential keywords. Estimated time: 30-60 seconds.
            </p>
            <Button onClick={handleDiscover} disabled={discovering}>
              {discovering ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Discovering...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Discover Ranking Keywords
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Keyword Discovery Results
          </CardTitle>
          <CardDescription>
            Keywords your site ranks for in the top 50 positions
          </CardDescription>
        </div>
        <div className="flex gap-2">
          {discoveries.length > 0 && !discovering && (
            <Button
              onClick={handleAddAll}
              disabled={addingAll}
              variant="default"
              size="sm"
            >
              {addingAll ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Adding All...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add All ({discoveries.length})
                </>
              )}
            </Button>
          )}
          <Button
            onClick={handleDiscover}
            disabled={discovering}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${discovering ? 'animate-spin' : ''}`} />
            Discover Again
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {discovering ? (
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 mx-auto animate-spin text-muted-foreground mb-2" />
            <p className="text-muted-foreground mb-2">Testing keywords...</p>
            <p className="text-sm text-muted-foreground">This may take 30-60 seconds</p>
          </div>
        ) : discoveries.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-2">No ranking keywords found in top 50 positions.</p>
            <p className="text-sm text-muted-foreground">
              Try adding more products to your site or wait for your site to rank for more keywords.
            </p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Keyword</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>SERP Features</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discoveries.map((discovery, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        {discovery.keyword}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {discovery.url}
                      </div>
                    </TableCell>
                    <TableCell>{renderPositionBadge(discovery.position)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {discovery.source.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {discovery.hasFeaturedSnippet && (
                          <Badge variant="outline" className="text-xs">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Snippet
                          </Badge>
                        )}
                        {discovery.hasPeopleAlsoAsk && (
                          <Badge variant="outline" className="text-xs">
                            <HelpCircle className="h-3 w-3 mr-1" />
                            PAA
                          </Badge>
                        )}
                        {!discovery.hasFeaturedSnippet && !discovery.hasPeopleAlsoAsk && (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => handleAddKeyword(discovery.keyword)}
                        disabled={addingKeyword === discovery.keyword}
                      >
                        {addingKeyword === discovery.keyword ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4 mr-2" />
                            Track
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="mt-4 p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Found {discoveries.length} ranking keywords.</strong> These are keywords from your product catalog
                that your site already ranks for. Click "Track" to start monitoring their performance over time.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
